import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Navigation from "../components/Navigation";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Calendar, Plus, Edit, Trash2, Clock, MapPin, Users, AlertTriangle, School, Trophy } from "lucide-react";
import { menuItems } from "../config/menuItems";
import axios from "axios";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function CalendarManagement({ user, onLogout }) {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [teams, setTeams] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [activeView, setActiveView] = useState("all"); // all, team, school, player
  const [selectedTeam, setSelectedTeam] = useState("");
  const [selectedPlayer, setSelectedPlayer] = useState("");
  const [players, setPlayers] = useState([]);

  const [eventFormData, setEventFormData] = useState({
    title: "",
    description: "",
    start_datetime: "",
    end_datetime: "",
    location: "",
    event_type: "practice",
    opponent: ""
  });

  // Check if user has calendar management permissions
  const hasPermission = ['coach', 'team-admin', 'system-admin', 'school-admin'].includes(user?.role);

  useEffect(() => {
    if (hasPermission) {
      fetchEvents();
      fetchTeams();
      fetchPlayers();
    }
  }, [hasPermission, activeView, selectedTeam, selectedPlayer]);

  const fetchEvents = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      
      if (activeView === "all") {
        endpoint = `${API}/calendar/events`;
      } else if (activeView === "team" && selectedTeam) {
        endpoint = `${API}/calendar/team/${selectedTeam}/events`;
      } else if (activeView === "school" && user.school) {
        endpoint = `${API}/calendar/school/${encodeURIComponent(user.school)}/events`;
      } else if (activeView === "player" && selectedPlayer) {
        endpoint = `${API}/calendar/player/${selectedPlayer}/events`;
      } else {
        endpoint = `${API}/calendar/events`;
      }

      const response = await axios.get(endpoint, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setEvents(response.data);
    } catch (error) {
      toast.error("Failed to load calendar events");
      console.error("Calendar events error:", error);
    }
    setLoading(false);
  };

  const fetchTeams = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/teams`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTeams(response.data);
    } catch (error) {
      console.error("Failed to fetch teams:", error);
    }
  };

  const fetchPlayers = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/users?role=athlete`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPlayers(response.data);
    } catch (error) {
      console.error("Failed to fetch players:", error);
    }
  };

  const handleCreateEvent = async () => {
    if (!eventFormData.title || !eventFormData.start_datetime || !eventFormData.end_datetime) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      let endpoint = `${API}/calendar/events`;
      
      // If creating for a specific team
      if (activeView === "team" && selectedTeam) {
        endpoint = `${API}/calendar/team/${selectedTeam}/events`;
      }

      await axios.post(endpoint, eventFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Calendar event created successfully!");
      setShowCreateDialog(false);
      resetEventForm();
      fetchEvents();
    } catch (error) {
      toast.error("Failed to create calendar event");
      console.error("Create event error:", error);
    }
  };

  const handleUpdateEvent = async () => {
    if (!selectedEvent || !eventFormData.title || !eventFormData.start_datetime || !eventFormData.end_datetime) {
      toast.error("Please fill in all required fields");
      return;
    }

    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/calendar/events/${selectedEvent.id}`, eventFormData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Calendar event updated successfully!");
      setShowEditDialog(false);
      resetEventForm();
      fetchEvents();
    } catch (error) {
      toast.error("Failed to update calendar event");
      console.error("Update event error:", error);
    }
  };

  const handleDeleteEvent = async (eventId) => {
    if (!confirm("Are you sure you want to delete this event?")) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/calendar/events/${eventId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      toast.success("Calendar event deleted successfully!");
      fetchEvents();
    } catch (error) {
      toast.error("Failed to delete calendar event");
      console.error("Delete event error:", error);
    }
  };

  const resetEventForm = () => {
    setEventFormData({
      title: "",
      description: "",
      start_datetime: "",
      end_datetime: "",
      location: "",
      event_type: "practice",
      opponent: ""
    });
    setSelectedEvent(null);
  };

  const openEditDialog = (event) => {
    setSelectedEvent(event);
    setEventFormData({
      title: event.title,
      description: event.description || "",
      start_datetime: new Date(event.start_datetime).toISOString().slice(0, 16),
      end_datetime: new Date(event.end_datetime).toISOString().slice(0, 16),
      location: event.location || "",
      event_type: event.event_type,
      opponent: event.opponent || ""
    });
    setShowEditDialog(true);
  };

  const formatDateTime = (dateString) => {
    return new Date(dateString).toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getEventTypeColor = (type) => {
    switch (type) {
      case 'practice': return 'bg-blue-100 text-blue-800';
      case 'game': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  if (!hasPermission) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-100">
        <Navigation user={user} onLogout={onLogout} />
        <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-12">
          <Card className="max-w-md mx-auto">
            <CardContent className="text-center p-8">
              <AlertTriangle className="w-16 h-16 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-bold text-gray-900 mb-2">Access Denied</h2>
              <p className="text-gray-600">
                You need coach or administrator permissions to manage calendars.
              </p>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 via-teal-50 to-green-100">
      <Navigation user={user} onLogout={onLogout} />

      <div className="max-w-7xl mx-auto px-3 sm:px-6 lg:px-8 py-4 sm:py-12">
        <div className="mb-8">
          <h1 className="text-2xl sm:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Calendar Management
          </h1>
          <p className="text-lg text-gray-600">
            Create and manage calendar events for teams, players, and schools
          </p>
        </div>

        {/* View Selection and Controls */}
        <Card className="mb-8">
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle>Calendar Views</CardTitle>
              <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
                <DialogTrigger asChild>
                  <Button className="bg-teal-600 hover:bg-teal-700" onClick={resetEventForm}>
                    <Plus className="w-4 h-4 mr-2" />
                    Create Event
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-md">
                  <DialogHeader>
                    <DialogTitle>Create Calendar Event</DialogTitle>
                    <DialogDescription>
                      Add a new event to the calendar
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-4">
                    <div>
                      <Label>Title</Label>
                      <Input
                        value={eventFormData.title}
                        onChange={(e) => setEventFormData(prev => ({...prev, title: e.target.value}))}
                        placeholder="Event title"
                      />
                    </div>
                    <div>
                      <Label>Description</Label>
                      <Input
                        value={eventFormData.description}
                        onChange={(e) => setEventFormData(prev => ({...prev, description: e.target.value}))}
                        placeholder="Event description"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Start Date & Time</Label>
                        <Input
                          type="datetime-local"
                          value={eventFormData.start_datetime}
                          onChange={(e) => setEventFormData(prev => ({...prev, start_datetime: e.target.value}))}
                        />
                      </div>
                      <div>
                        <Label>End Date & Time</Label>
                        <Input
                          type="datetime-local"
                          value={eventFormData.end_datetime}
                          onChange={(e) => setEventFormData(prev => ({...prev, end_datetime: e.target.value}))}
                        />
                      </div>
                    </div>
                    <div>
                      <Label>Location</Label>
                      <Input
                        value={eventFormData.location}
                        onChange={(e) => setEventFormData(prev => ({...prev, location: e.target.value}))}
                        placeholder="Event location"
                      />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Event Type</Label>
                        <select
                          value={eventFormData.event_type}
                          onChange={(e) => setEventFormData(prev => ({...prev, event_type: e.target.value}))}
                          className="w-full border border-gray-300 rounded-md px-3 py-2"
                        >
                          <option value="practice">Practice</option>
                          <option value="game">Game</option>
                          <option value="other">Other</option>
                        </select>
                      </div>
                      <div>
                        <Label>Opponent (for games)</Label>
                        <Input
                          value={eventFormData.opponent}
                          onChange={(e) => setEventFormData(prev => ({...prev, opponent: e.target.value}))}
                          placeholder="Opponent name"
                          disabled={eventFormData.event_type !== 'game'}
                        />
                      </div>
                    </div>
                    <div className="flex justify-end space-x-2">
                      <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                        Cancel
                      </Button>
                      <Button onClick={handleCreateEvent} className="bg-teal-600 hover:bg-teal-700">
                        Create Event
                      </Button>
                    </div>
                  </div>
                </DialogContent>
              </Dialog>
            </div>
          </CardHeader>
          <CardContent>
            <div className="flex flex-wrap gap-4 mb-4">
              <Button
                variant={activeView === "all" ? "default" : "outline"}
                onClick={() => setActiveView("all")}
                className={activeView === "all" ? "bg-teal-600 hover:bg-teal-700" : ""}
              >
                All Events
              </Button>
              
              {user.role !== 'athlete' && (
                <Button
                  variant={activeView === "team" ? "default" : "outline"}
                  onClick={() => setActiveView("team")}
                  className={activeView === "team" ? "bg-teal-600 hover:bg-teal-700" : ""}
                >
                  <Trophy className="w-4 h-4 mr-2" />
                  Team Events
                </Button>
              )}
              
              {(user.role === 'school-admin' || user.role === 'system-admin') && (
                <Button
                  variant={activeView === "school" ? "default" : "outline"}
                  onClick={() => setActiveView("school")}
                  className={activeView === "school" ? "bg-teal-600 hover:bg-teal-700" : ""}
                >
                  <School className="w-4 h-4 mr-2" />
                  School Events
                </Button>
              )}
              
              <Button
                variant={activeView === "player" ? "default" : "outline"}
                onClick={() => setActiveView("player")}
                className={activeView === "player" ? "bg-teal-600 hover:bg-teal-700" : ""}
              >
                <Users className="w-4 h-4 mr-2" />
                Player Events
              </Button>
            </div>

            {/* Filters based on active view */}
            {activeView === "team" && (
              <div className="mb-4">
                <Label>Select Team</Label>
                <select
                  value={selectedTeam}
                  onChange={(e) => setSelectedTeam(e.target.value)}
                  className="w-full md:w-auto border border-gray-300 rounded-md px-3 py-2 ml-2"
                >
                  <option value="">All Teams</option>
                  {teams.map((team) => (
                    <option key={team.id} value={team.id}>{team.name}</option>
                  ))}
                </select>
              </div>
            )}
            
            {activeView === "player" && (
              <div className="mb-4">
                <Label>Select Player</Label>
                <select
                  value={selectedPlayer}
                  onChange={(e) => setSelectedPlayer(e.target.value)}
                  className="w-full md:w-auto border border-gray-300 rounded-md px-3 py-2 ml-2"
                >
                  <option value="">Select a player</option>
                  {players.map((player) => (
                    <option key={player.id} value={player.id}>{player.name}</option>
                  ))}
                </select>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Calendar Events Display */}
        {loading ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-600 mx-auto"></div>
            <p className="text-gray-600 mt-4">Loading calendar events...</p>
          </div>
        ) : events.length === 0 ? (
          <Card>
            <CardContent className="text-center py-12">
              <Calendar className="w-16 h-16 text-gray-400 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No events found</h3>
              <p className="text-gray-600">Create your first calendar event to get started.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
            {events.map((event) => (
              <Card key={event.id} className="hover:shadow-lg transition-shadow">
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <CardTitle className="text-lg">{event.title}</CardTitle>
                      <div className="flex items-center space-x-2 mt-2">
                        <Badge className={getEventTypeColor(event.event_type)}>
                          {event.event_type}
                        </Badge>
                        {event.opponent && (
                          <Badge variant="outline">vs {event.opponent}</Badge>
                        )}
                      </div>
                    </div>
                    <div className="flex space-x-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => openEditDialog(event)}
                      >
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => handleDeleteEvent(event.id)}
                      >
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <Clock className="w-4 h-4" />
                      <span>{formatDateTime(event.start_datetime)}</span>
                    </div>
                    {event.location && (
                      <div className="flex items-center space-x-2">
                        <MapPin className="w-4 h-4" />
                        <span>{event.location}</span>
                      </div>
                    )}
                    {event.description && (
                      <p className="mt-2">{event.description}</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Edit Event Dialog */}
        <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>Edit Calendar Event</DialogTitle>
              <DialogDescription>
                Update the event details
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Title</Label>
                <Input
                  value={eventFormData.title}
                  onChange={(e) => setEventFormData(prev => ({...prev, title: e.target.value}))}
                  placeholder="Event title"
                />
              </div>
              <div>
                <Label>Description</Label>
                <Input
                  value={eventFormData.description}
                  onChange={(e) => setEventFormData(prev => ({...prev, description: e.target.value}))}
                  placeholder="Event description"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Start Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={eventFormData.start_datetime}
                    onChange={(e) => setEventFormData(prev => ({...prev, start_datetime: e.target.value}))}
                  />
                </div>
                <div>
                  <Label>End Date & Time</Label>
                  <Input
                    type="datetime-local"
                    value={eventFormData.end_datetime}
                    onChange={(e) => setEventFormData(prev => ({...prev, end_datetime: e.target.value}))}
                  />
                </div>
              </div>
              <div>
                <Label>Location</Label>
                <Input
                  value={eventFormData.location}
                  onChange={(e) => setEventFormData(prev => ({...prev, location: e.target.value}))}
                  placeholder="Event location"
                />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Event Type</Label>
                  <select
                    value={eventFormData.event_type}
                    onChange={(e) => setEventFormData(prev => ({...prev, event_type: e.target.value}))}
                    className="w-full border border-gray-300 rounded-md px-3 py-2"
                  >
                    <option value="practice">Practice</option>
                    <option value="game">Game</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <Label>Opponent (for games)</Label>
                  <Input
                    value={eventFormData.opponent}
                    onChange={(e) => setEventFormData(prev => ({...prev, opponent: e.target.value}))}
                    placeholder="Opponent name"
                    disabled={eventFormData.event_type !== 'game'}
                  />
                </div>
              </div>
              <div className="flex justify-end space-x-2">
                <Button variant="outline" onClick={() => setShowEditDialog(false)}>
                  Cancel
                </Button>
                <Button onClick={handleUpdateEvent} className="bg-teal-600 hover:bg-teal-700">
                  Update Event
                </Button>
              </div>
            </div>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}